// Cards.tsx
import React, { useCallback, useEffect, useState } from 'react'
import {
  collection,
  doc,
  DocumentSnapshot,
  query,
  QuerySnapshot,
} from 'firebase/firestore'
import { db } from '@/helpers/firebase'
import { useTonAddress } from '@tonconnect/ui-react'
import { HearthstoneRevealModal } from '@/screens/Modals/HearthstoneRevealModal'
import { UserCard } from 'common/schema/users/userCards'
import { callOpenCard, callForge } from '@/helpers/api'
import { Package, Zap, Flame } from 'lucide-react'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import '@/screens/Cards.css'
import { RarityType } from 'common/schema/cards'
import { Card } from '@/screens/Card'
import { ForgeResultModal } from '@/screens/Modals/ForgeResultModal'
import {
  useFirestoreCollectionCache,
  useFirestoreDocCache,
} from '@/hooks/useFirestoreCache'

import { RARITY_ORDER, FORGE_RULES, FORGE_TOKEN_COSTS } from 'common/types'

export type RevealItem = {
  type: 'Fragment' | 'UserCard' | 'Unrevealed'
  url: string
  rarity?: RarityType
  name?: string
  timesOpened?: number
}

type Mode = 'view' | 'forge'

export const Cards: React.FC = () => {
  const [userBalance, setUserBalance] = useState<number>(0)
  const [insufficientBalance, setInsufficientBalance] = useState(false)
  const [activeView, setActiveView] = useState<'unrevealed' | 'revealed'>(
    'unrevealed'
  )
  const [selectedRarity, setSelectedRarity] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('view')
  const [selectedCards, setSelectedCards] = useState<UserCard[]>([])
  const [isForging, setIsForging] = useState(false)

  // Reveal modal states
  const [selectedCard, setSelectedCard] = useState<UserCard | null>(null)
  const [isRevealModalOpen, setIsRevealModalOpen] = useState(false)
  const [isRevealing, setIsRevealing] = useState(false)
  const [revealedItems, setRevealedItems] = useState<RevealItem[]>([])
  const [tokenReward, setTokenReward] = useState<number>(0)

  const [showForgeResult, setShowForgeResult] = useState(false)
  const [forgeResult, setForgeResult] = useState<{
    success: boolean
    resultCard: UserCard | null
    tokensCost: number
    cardsConsumed: number
  } | null>(null)

  const walletAddress = useTonAddress()

  // Memoize transform functions
  const transformUserData = useCallback(
    (snapshot: DocumentSnapshot) => snapshot.data() || null,
    []
  )

  const transformUserCards = useCallback((snapshot: QuerySnapshot) => {
    const cards: UserCard[] = []
    snapshot.forEach((doc) => {
      cards.push({ ...doc.data(), userCardId: doc.id } as UserCard)
    })
    return cards.sort(
      (a, b) =>
        (RARITY_ORDER[b.rarity as keyof typeof RARITY_ORDER] || 0) -
        (RARITY_ORDER[a.rarity as keyof typeof RARITY_ORDER] || 0)
    )
  }, [])

  // Use the hooks with memoized transform functions
  const { data: userData } = useFirestoreDocCache(
    walletAddress ? doc(db, 'users', walletAddress) : null,
    `userData-${walletAddress}`,
    transformUserData,
    !!walletAddress
  )

  const {
    data: userCards = [],
    loading,
    error,
  } = useFirestoreCollectionCache<UserCard>(
    walletAddress
      ? query(collection(db, 'users', walletAddress, 'userCards'))
      : null,
    `userCards-${walletAddress}`,
    transformUserCards,
    !!walletAddress
  )

  // Update userBalance when userData changes
  useEffect(() => {
    if (userData) {
      setUserBalance(userData.tokenBalance || 0)
    }
  }, [userData])

  const handleCardClick = useCallback(
    (card: UserCard) => {
      if (mode === 'forge') {
        if (card.rarity === 'Original') return

        setSelectedCards((prevSelected) => {
          // Check if card is already selected
          const isSelected = prevSelected.some(
            (c) => c.userCardId === card.userCardId
          )

          if (isSelected) {
            // Remove card if already selected
            return prevSelected.filter((c) => c.userCardId !== card.userCardId)
          }

          // If no cards selected yet, start new selection
          if (prevSelected.length === 0) {
            return [card]
          }

          // Check if new card matches rarity of already selected cards
          if (card.rarity === prevSelected[0].rarity) {
            const maxRequired =
              FORGE_RULES[card.rarity as keyof typeof FORGE_RULES]?.required ||
              0
            if (prevSelected.length < maxRequired) {
              return [...prevSelected, card]
            }
          }

          return prevSelected
        })
      } else if (card.status !== 'complete') {
        setSelectedCard(card)
        setIsRevealModalOpen(true)
        setRevealedItems([])
      }
    },
    [mode]
  )

  const handleReveal = async () => {
    if (!selectedCard || isRevealing) return

    setIsRevealing(true)
    try {
      const result = await callOpenCard({ userCardId: selectedCard.userCardId })
      if (result.success) {
        const items: RevealItem[] = []
        if (result.response.data.fragment) {
          items.push({
            type: 'Fragment',
            url: result.response.data.fragment.url,
            rarity: result.response.data.fragment.rarity,
            name: result.response.data.fragment.name,
          })
        }
        if (result.response.data.revealedCard) {
          items.push({
            type: 'UserCard',
            url: result.response.data.revealedCard.url || '',
            rarity: result.response.data.revealedCard.rarity,
            name: result.response.data.revealedCard.name,
            timesOpened: result.response.data.revealedCard.timesOpened,
          })
        }
        if (result.response.data.newUnrevealedCard) {
          items.push({
            type: 'Unrevealed',
            url: result.response.data.newUnrevealedCard.url || '',
            rarity: result.response.data.newUnrevealedCard.rarity,
            name: result.response.data.newUnrevealedCard.name,
          })
        }
        if (result.response.data.tokenReward) {
          setTokenReward(result.response.data.tokenReward)
        }
        setRevealedItems(items)
      }
    } catch (error) {
      console.error('Reveal error:', error)
    } finally {
      setIsRevealing(false)
    }
  }

  const handleForge = async () => {
    if (!selectedCards.length || isForging || !walletAddress) return

    const rarity = selectedCards[0].rarity
    const rules = FORGE_RULES[rarity as keyof typeof FORGE_RULES]
    const forgeCost =
      FORGE_TOKEN_COSTS[rarity as keyof typeof FORGE_TOKEN_COSTS]

    if (!rules || selectedCards.length < rules.required) return

    if (userBalance < forgeCost) {
      setInsufficientBalance(true)
      setTimeout(() => setInsufficientBalance(false), 3000)
      return
    }

    setIsForging(true)
    try {
      const result = await callForge({
        userCardIds: selectedCards.map((card) => card.userCardId),
      })

      if (result.success) {
        setForgeResult({
          success: result.response.data.forgeSuccess,
          resultCard: result.response.data.resultCard,
          tokensCost: result.response.data.tokensCost,
          cardsConsumed: result.response.data.cardsConsumed,
        })
        setShowForgeResult(true)
        setSelectedCards([])
        setMode('view')
      }
    } catch (error) {
      console.error('Forge failed:', error)
    } finally {
      setIsForging(false)
    }
  }

  const filteredCards =
    userCards?.filter((card) => {
      if (mode === 'forge' && card.rarity === 'Original') {
        return false
      }
      return (
        card.status === activeView &&
        (!selectedRarity || card.rarity === selectedRarity)
      )
    }) || []

  const renderTokenWarning = () => {
    if (!insufficientBalance) return null

    return (
      <div className="token-warning">
        <div className="token-warning-content">
          <span className="token-warning-icon">⚠️</span>
          <span>Insufficient token balance!</span>
          <span className="token-warning-balance">
            Balance: {userBalance} ₮
          </span>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
        <p>Loading your cards...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="error-state">
        <p>{error.message}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    )
  }

  return (
    <ProtectedRoute>
      <div className="cards-container">
        {/* Main Actions */}
        <div className="cards-actions">
          <div className="view-toggle">
            <button
              className={`action-button ${activeView === 'unrevealed' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('unrevealed')
                setSelectedCards([])
              }}
            >
              <Package size={20} />
              Unrevealed (
              {userCards?.filter((c) => c.status === 'unrevealed').length})
            </button>
            <button
              className={`action-button ${activeView === 'revealed' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('revealed')
                setSelectedCards([])
              }}
            >
              <Zap size={20} />
              Revealed (
              {userCards?.filter((c) => c.status === 'revealed').length})
            </button>
          </div>
          <button
            className={`forge-toggle ${mode === 'forge' ? 'active' : ''}`}
            onClick={() => {
              setMode(mode === 'forge' ? 'view' : 'forge')
              setSelectedCards([])
            }}
          >
            <Flame size={20} />
            {mode === 'forge' ? 'Cancel Forge' : 'Forge Cards'}
          </button>
        </div>

        {/* Rest of your existing JSX */}
        {/* Rarity Filters */}
        <div className="rarity-filters">
          {Object.keys(RARITY_ORDER).map((rarity) => (
            <button
              key={rarity}
              className={`rarity-filter ${selectedRarity === rarity ? 'active' : ''}`}
              onClick={() =>
                setSelectedRarity(rarity === selectedRarity ? null : rarity)
              }
            >
              {rarity}
            </button>
          ))}
        </div>
        {renderTokenWarning()}

        {mode === 'forge' && (
          <div className="forge-instructions">
            {selectedCards.length === 0 ? (
              <>
                <p>
                  Select cards of the same rarity to forge them into a higher
                  rarity card
                </p>
                <div className="forge-cost-list">
                  <p className="forge-cost-title">Forge Costs:</p>
                  <div className="forge-cost-grid">
                    <div className="forge-cost-item">
                      <span
                        className={`reveal-rarity-badge reveal-rarity-common`}
                      >
                        Common
                      </span>
                      <span className="forge-cost-tokens"> 100 ₮</span>
                    </div>
                    <div className="forge-cost-item">
                      <span
                        className={`reveal-rarity-badge reveal-rarity-uncommon`}
                      >
                        Uncommon
                      </span>
                      <span className="forge-cost-tokens"> 300 ₮</span>
                    </div>
                    <div className="forge-cost-item">
                      <span
                        className={`reveal-rarity-badge reveal-rarity-rare`}
                      >
                        Rare
                      </span>
                      <span className="forge-cost-tokens"> 1,000 ₮</span>
                    </div>
                    <div className="forge-cost-item">
                      <span
                        className={`reveal-rarity-badge reveal-rarity-epic`}
                      >
                        Epic
                      </span>
                      <span className="forge-cost-tokens"> 3,000 ₮</span>
                    </div>
                    <div className="forge-cost-item">
                      <span
                        className={`reveal-rarity-badge reveal-rarity-legendary`}
                      >
                        Legendary
                      </span>
                      <span className="forge-cost-tokens"> 10,000 ₮</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p>
                Selected {selectedCards.length} /{' '}
                {
                  FORGE_RULES[
                    selectedCards[0].rarity as keyof typeof FORGE_RULES
                  ]?.required
                }{' '}
                {selectedCards[0].rarity} cards{', '}
                <span className="forge-required-tokens">
                  Cost:{' '}
                  {FORGE_TOKEN_COSTS[
                    selectedCards[0].rarity as keyof typeof FORGE_TOKEN_COSTS
                  ].toLocaleString()}{' '}
                  ₮
                </span>
              </p>
            )}
          </div>
        )}

        {/* Cards Grid */}
        <div className="cards-grid">
          {filteredCards.map((card) => (
            <div
              key={card.userCardId}
              className={`cards-grid-item ${mode === 'forge' ? 'selectable' : ''} ${
                selectedCards.some((c) => c.userCardId === card.userCardId)
                  ? 'selected'
                  : ''
              }`}
            >
              <Card
                card={card}
                isSelected={selectedCards.some(
                  (c) => c.userCardId === card.userCardId
                )}
                onClick={() => handleCardClick(card)}
              />
            </div>
          ))}
        </div>

        {/* Forge Controls */}
        {mode === 'forge' && selectedCards.length > 0 && (
          <div className="forge-controls">
            <div className="forge-info">
              <span className="forge-balance">Balance: {userBalance} ₮</span>
              <span className="forge-cost">
                Required:{' '}
                {
                  FORGE_TOKEN_COSTS[
                    selectedCards[0].rarity as keyof typeof FORGE_TOKEN_COSTS
                  ]
                }{' '}
                ₮
              </span>
            </div>
            <button
              className={`forge-button ${userBalance < FORGE_TOKEN_COSTS[selectedCards[0].rarity as keyof typeof FORGE_TOKEN_COSTS] ? 'insufficient' : ''}`}
              disabled={
                isForging ||
                selectedCards.length <
                  FORGE_RULES[
                    selectedCards[0].rarity as keyof typeof FORGE_RULES
                  ]?.required
              }
              onClick={handleForge}
            >
              {isForging ? (
                <>
                  <div className="loading-spinner" />
                  Forging...
                </>
              ) : (
                <>
                  <Flame size={20} />
                  Forge {selectedCards.length} Cards
                </>
              )}
            </button>
          </div>
        )}

        {/* Modals */}
        {selectedCard && (
          <HearthstoneRevealModal
            isOpen={isRevealModalOpen}
            onClose={() => {
              setIsRevealModalOpen(false)
              setSelectedCard(null)
              setRevealedItems([])
            }}
            initialCardUrl={selectedCard.url || ''}
            onReveal={handleReveal}
            revealedItems={revealedItems}
            isRevealing={isRevealing}
            tokenReward={tokenReward}
          />
        )}

        {forgeResult && (
          <ForgeResultModal
            isOpen={showForgeResult}
            onClose={() => {
              setShowForgeResult(false)
              setForgeResult(null)
            }}
            isSuccess={forgeResult.success}
            resultCard={forgeResult.resultCard}
            tokensCost={forgeResult.tokensCost}
            cardsConsumed={forgeResult.cardsConsumed}
          />
        )}
      </div>
    </ProtectedRoute>
  )
}
